import { validateCompletion } from './completionValidation'
import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import { canonicalOfflineCommand, type OfflineWireBatch, type OfflineWireMutation } from './wire'
import type { CaptureScopeV2, CaptureSnapshotV2, CaptureStorageV2, CaptureSyncResultV2, EnqueueCaptureV2, MutationStateV2, ResolveAttentionV2, StoredMutationV2 } from './typesV2'

const DATABASE_NAME = 'coachsight-capture-v2'
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/
const DIGEST = /^sha256:[0-9a-f]{64}$/
const REVISION = /^(0|[1-9][0-9]*)$/
const K = [0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2]
const ROTR = (x: number, n: number): number => (x >>> n) | (x << (32 - n))

/** Synchronous SHA-256 permits hash and sequence to commit in one IDB transaction. */
export function sha256Hex(input: string): string {
  const bytes = new TextEncoder().encode(input)
  const padded = new Uint8Array(Math.ceil((bytes.length + 9) / 64) * 64)
  padded.set(bytes); padded[bytes.length] = 0x80
  const bitLength = bytes.length * 8
  const tail = padded.length - 8
  new DataView(padded.buffer).setUint32(tail, Math.floor(bitLength / 2 ** 32))
  new DataView(padded.buffer).setUint32(tail + 4, bitLength >>> 0)
  const h = [0x6a09e667,0xbb67ae85,0x3c6ef372,0xa54ff53a,0x510e527f,0x9b05688c,0x1f83d9ab,0x5be0cd19]
  const words = new Uint32Array(64)
  const view = new DataView(padded.buffer)
  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let i = 0; i < 16; i++) words[i] = view.getUint32(offset + i * 4)
    for (let i = 16; i < 64; i++) {
      const a = words[i - 15], b = words[i - 2]
      words[i] = (words[i - 16] + (ROTR(a, 7) ^ ROTR(a, 18) ^ (a >>> 3)) + words[i - 7] + (ROTR(b, 17) ^ ROTR(b, 19) ^ (b >>> 10))) >>> 0
    }
    let [a,b,c,d,e,f,g,hh] = h
    for (let i = 0; i < 64; i++) {
      const choose = (e & f) ^ (~e & g)
      const majority = (a & b) ^ (a & c) ^ (b & c)
      const t1 = (hh + (ROTR(e, 6) ^ ROTR(e, 11) ^ ROTR(e, 25)) + choose + K[i] + words[i]) >>> 0
      const t2 = ((ROTR(a, 2) ^ ROTR(a, 13) ^ ROTR(a, 22)) + majority) >>> 0
      hh = g; g = f; f = e; e = (d + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0
    }
    for (const [i, value] of [a,b,c,d,e,f,g,hh].entries()) h[i] = (h[i] + value) >>> 0
  }
  return h.map(word => word.toString(16).padStart(8, '0')).join('')
}

interface StoredRow { id: string; scopeKey: string; recordKey: string; sequence: number; state: MutationStateV2 }
interface CaptureDBV2 extends DBSchema {
  metadata: { key: string; value: { key: string; value: number | string } }
  snapshots: { key: string; value: { key: string; scopeKey: string; snapshot: CaptureSnapshotV2 } }
  mutations: { key: string; value: StoredRow; indexes: { 'by-scope-order': [string, number]; 'by-record-order': [string, string, number] } }
}

const clone = <T>(value: T): T => structuredClone(value)
const required = (value: string, field: string): void => { if (typeof value !== 'string' || !value.trim()) throw new Error(`${field} is required`) }
const uuid = (value: string, field: string): void => { if (typeof value !== 'string' || !UUID.test(value)) throw new Error(`${field} must be a lowercase UUID`) }
const instant = (value: string, field: string): number => {
  required(value, field)
  if (!/(?:Z|[+-]\d\d:\d\d)$/i.test(value) || !Number.isFinite(Date.parse(value))) throw new Error(`${field} must be an offset-bearing instant`)
  return Date.parse(value)
}
const canonical = (value: unknown): string => {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return JSON.stringify(value)
  if (typeof value === 'number' && Number.isFinite(value)) return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`
  if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    const fields = Object.entries(value).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0)
    for (const [key] of fields) if (/(token|authorization|password|secret|cookie|credential)/i.test(key)) throw new Error(`Sensitive field ${key} cannot be stored`)
    return `{${fields.map(([key, entry]) => `${JSON.stringify(key)}:${canonical(entry)}`).join(',')}}`
  }
  throw new Error('Capture data must be plain finite JSON')
}
const abort = async (tx: { abort(): void; done: Promise<unknown> }, error: Error): Promise<never> => {
  try { tx.abort() } catch { /* Already aborted. */ }
  try { await tx.done } catch { /* Observe expected rejection. */ }
  throw error
}

export function captureScopeKeyV2(scope: CaptureScopeV2): string {
  for (const field of ['realActorId','effectiveActorId','clubId','seasonId','eventId','sessionId'] as const) uuid(scope[field], field)
  if (scope.realActorId !== scope.effectiveActorId) throw new Error('Masquerade capture is disabled')
  const origin = new URL(scope.apiOrigin)
  if (origin.origin !== scope.apiOrigin || !['http:', 'https:'].includes(origin.protocol)) throw new Error('apiOrigin must be a canonical HTTP origin')
  return JSON.stringify([origin.origin,scope.realActorId,scope.effectiveActorId,scope.clubId,scope.seasonId,scope.eventId,scope.sessionId])
}
const activeKey = (scopeKey: string): string => JSON.stringify(['active',scopeKey])
const snapshotKey = (scopeKey: string, id: string): string => JSON.stringify([scopeKey,id])
const recordKey = (scope: CaptureScopeV2, key: OfflineWireMutation['recordKey']): string =>
  JSON.stringify([scope.eventId,key.participantId,scope.effectiveActorId,key.criterionId,key.sessionId,key.activityId ?? '0','0'])

function validateSnapshot(scope: CaptureScopeV2, snapshot: CaptureSnapshotV2): void {
  uuid(snapshot.snapshotId,'snapshotId'); uuid(snapshot.actorId,'actorId'); uuid(snapshot.eventId,'eventId'); uuid(snapshot.sessionId,'sessionId'); uuid(snapshot.dataEpoch,'dataEpoch')
  if (snapshot.actorId !== scope.effectiveActorId || snapshot.eventId !== scope.eventId || snapshot.sessionId !== scope.sessionId) throw new Error('Snapshot actor or event scope mismatch')
  if (!DIGEST.test(snapshot.configurationRevision) || !/^[0-9a-f]{64}$/.test(snapshot.snapshotHash)) throw new Error('Snapshot digest is invalid')
  const prepared = instant(snapshot.preparedAt,'preparedAt'), end = instant(snapshot.sessionEndsAt,'sessionEndsAt'), until = instant(snapshot.uploadUntil,'uploadUntil')
  if (prepared >= end || until !== end + 24 * 60 * 60 * 1000) throw new Error('Snapshot capture window is invalid')
  if (!snapshot.resources || !snapshot.counts) throw new Error('Snapshot resources are incomplete')
  for (const name of ['participants','criteria','activities','activityCriteria','descriptorCategories','descriptorItems','assignments','scores','tombstones'] as const) {
    if (!Array.isArray(snapshot.resources[name]) || snapshot.counts[name] !== snapshot.resources[name].length) throw new Error(`Snapshot ${name} is incomplete`)
  }
  if (snapshot.resources.session?.id !== scope.sessionId || instant(snapshot.resources.session.endsAt,'resources.session.endsAt') !== end) throw new Error('Snapshot session mismatch')
  for (const row of [...snapshot.resources.scores,...snapshot.resources.tombstones]) {
    const key = row.recordKey
    if (key.eventId !== scope.eventId || key.staffId !== scope.effectiveActorId || key.sessionId !== scope.sessionId || key.episodeId !== '0') throw new Error('Snapshot includes another actor or session')
    if (!REVISION.test(row.revision)) throw new Error('Snapshot revision is invalid')
  }
  validateCompletion(snapshot)
  canonical(snapshot)
}

function validateInput(scope: CaptureScopeV2, input: EnqueueCaptureV2): void {
  if (input.clientMutationId !== undefined) uuid(input.clientMutationId,'clientMutationId')
  uuid(input.expectedSnapshotId,'expectedSnapshotId')
  required(input.deviceId,'deviceId'); if (input.deviceId.length > 128) throw new Error('deviceId exceeds 128 characters')
  for (const field of ['participantId','criterionId','sessionId'] as const) uuid(input.recordKey[field],field)
  if (input.recordKey.sessionId !== scope.sessionId) throw new Error('Record session differs from capture scope')
  if (input.recordKey.activityId !== null && input.recordKey.activityId !== '0') uuid(input.recordKey.activityId,'activityId')
  instant(input.capturedAt,'capturedAt')
  if (!['simple','feedback'].includes(input.payload.scoringMode) || !Number.isFinite(input.payload.score) || !Array.isArray(input.payload.descriptorSelections)) throw new Error('Only scalar capture is supported')
  if (input.payload.globalScore !== undefined && (!Number.isInteger(input.payload.globalScore)||input.payload.globalScore<0||input.payload.globalScore>5)) throw new Error('Invalid global score')
  if (input.payload.counter !== undefined && !Number.isFinite(input.payload.counter)) throw new Error('Counter must be finite')
  if (input.payload.buckets !== undefined && (!input.payload.buckets || Array.isArray(input.payload.buckets) || typeof input.payload.buckets !== 'object' || Object.values(input.payload.buckets).some(value => !Number.isFinite(value)))) throw new Error('Buckets must be finite numbers')
  for (const item of input.payload.descriptorSelections) {
    uuid(item.categoryId,'categoryId'); uuid(item.itemId,'itemId')
    if (!Number.isFinite(item.levelValue)) throw new Error('Descriptor level must be finite')
  }
  canonical(input)
}

export class IndexedDBCaptureStorageV2 implements CaptureStorageV2 {
  private dbPromise?: Promise<IDBPDatabase<CaptureDBV2>>
  private enqueueTail: Promise<void> = Promise.resolve()
  constructor(private readonly databaseName = DATABASE_NAME) {}
  private db(): Promise<IDBPDatabase<CaptureDBV2>> {
    this.dbPromise ??= openDB<CaptureDBV2>(this.databaseName, 1, {
      upgrade(db, oldVersion) {
        if (oldVersion !== 0) throw new Error('Unsupported capture v2 database migration')
        db.createObjectStore('metadata',{keyPath:'key'})
        db.createObjectStore('snapshots',{keyPath:'key'})
        const mutations = db.createObjectStore('mutations',{keyPath:'id'})
        mutations.createIndex('by-scope-order',['scopeKey','sequence'])
        mutations.createIndex('by-record-order',['scopeKey','recordKey','sequence'])
      },
    }).catch(error => { this.dbPromise = undefined; throw error })
    return this.dbPromise
  }
  async stageSnapshot(scope: CaptureScopeV2, snapshot: CaptureSnapshotV2): Promise<void> {
    const stableScope=clone(scope), stable=clone(snapshot), key=captureScopeKeyV2(stableScope)
    validateSnapshot(stableScope,stable)
    const tx=(await this.db()).transaction('snapshots','readwrite',{durability:'strict'})
    const id=snapshotKey(key,stable.snapshotId), existing=await tx.store.get(id)
    if (existing && canonical(existing.snapshot)!==canonical(stable)) return abort(tx,new Error('Snapshot ID already has different data'))
    if (!existing) await tx.store.add({key:id,scopeKey:key,snapshot:stable})
    await tx.done
  }
  async activateSnapshot(scope: CaptureScopeV2, snapshotId: string): Promise<void> {
    const stableScope=clone(scope), key=captureScopeKeyV2(stableScope)
    uuid(snapshotId,'snapshotId')
    const tx=(await this.db()).transaction(['metadata','snapshots','mutations'],'readwrite',{durability:'strict'})
    const staged=await tx.objectStore('snapshots').get(snapshotKey(key,snapshotId))
    if (!staged) return abort(tx,new Error('Only a complete staged snapshot can be activated'))
    try { validateSnapshot(stableScope,staged.snapshot) } catch (error) { return abort(tx,error as Error) }
    const rows=await tx.objectStore('mutations').index('by-scope-order').getAll(IDBKeyRange.bound([key,0],[key,Number.MAX_SAFE_INTEGER]))
    if (rows.some(row=>row.state.status==='pending')) return abort(tx,new Error('Synchronize pending capture before changing snapshot'))
    await tx.objectStore('metadata').put({key:activeKey(key),value:snapshotId})
    await tx.done
  }
  async getActiveSnapshot(scope: CaptureScopeV2): Promise<CaptureSnapshotV2 | undefined> {
    const stableScope=clone(scope), key=captureScopeKeyV2(stableScope), db=await this.db()
    const active=await db.get('metadata',activeKey(key))
    if (typeof active?.value !== 'string') return undefined
    const row=await db.get('snapshots',snapshotKey(key,active.value))
    if (!row) throw new Error('Active capture snapshot is missing')
    validateSnapshot(stableScope,row.snapshot)
    return clone(row.snapshot)
  }
  /** Lists active preparation scopes for one signed-in account without reading snapshots. */
  async listPreparedScopes(apiOrigin: string, actorId: string): Promise<CaptureScopeV2[]> {
    uuid(actorId, 'actorId')
    const origin = new URL(apiOrigin)
    if (origin.origin !== apiOrigin || !['http:', 'https:'].includes(origin.protocol)) throw new Error('apiOrigin must be a canonical HTTP origin')
    const db = await this.db()
    const tx = db.transaction('metadata', 'readonly')
    const scopes: CaptureScopeV2[] = []
    const prefix = '["active",'
    let cursor = await tx.store.openCursor(IDBKeyRange.bound(prefix, `${prefix}\uffff`))
    while (cursor) {
      try {
        const [kind, rawScope] = JSON.parse(cursor.key) as [unknown, unknown]
        if (kind === 'active' && typeof rawScope === 'string' && typeof cursor.value.value === 'string' && UUID.test(cursor.value.value)) {
          const parts = JSON.parse(rawScope) as unknown
          if (Array.isArray(parts) && parts.length === 7 && parts.every(part => typeof part === 'string')) {
            const [storedOrigin, realActorId, effectiveActorId, clubId, seasonId, eventId, sessionId] = parts as string[]
            if (storedOrigin === apiOrigin && realActorId === actorId && effectiveActorId === actorId) {
              const scope = { apiOrigin: storedOrigin, realActorId, effectiveActorId, clubId, seasonId, eventId, sessionId }
              if (cursor.key === activeKey(captureScopeKeyV2(scope))) scopes.push(scope)
            }
          }
        }
      } catch { /* Ignore malformed metadata keys and scopes. */ }
      cursor = await cursor.continue()
    }
    await tx.done
    return scopes
  }
  enqueue(scope: CaptureScopeV2, input: EnqueueCaptureV2): Promise<StoredMutationV2> {
    const stableScope=clone(scope), stable=clone(input)
    const task=this.enqueueTail.then(()=>this.enqueueOne(stableScope,stable))
    this.enqueueTail=task.then(()=>undefined,()=>undefined)
    return task
  }
  private async enqueueOne(scope: CaptureScopeV2, input: EnqueueCaptureV2): Promise<StoredMutationV2> {
    const key=captureScopeKeyV2(scope)
    validateInput(scope,input)
    const id=input.clientMutationId ?? crypto.randomUUID()
    uuid(id,'clientMutationId')
    const db=await this.db(), tx=db.transaction(['metadata','snapshots','mutations'],'readwrite',{durability:'strict'})
    try {
      const mutations=tx.objectStore('mutations'), existing=await mutations.get(id)
      if (existing) {
        const prior=existing.state.mutation
        if (existing.scopeKey!==key || prior.snapshotId!==input.expectedSnapshotId || prior.deviceId!==input.deviceId || canonical(prior.wireMutation.recordKey)!==canonical({ ...input.recordKey,activityId:input.recordKey.activityId ?? '0' }) ||
          prior.wireMutation.capturedAt!==input.capturedAt || canonical(prior.wireMutation.payload)!==canonical(input.payload)) {
          return abort(tx,new Error('Mutation ID already exists with different content'))
        }
        await tx.done; return clone(prior)
      }
      const active=await tx.objectStore('metadata').get(activeKey(key))
      if (typeof active?.value!=='string') return abort(tx,new Error('No active prepared capture snapshot'))
      if (active.value!==input.expectedSnapshotId) return abort(tx,new Error('Displayed capture snapshot is no longer active'))
      const staged=await tx.objectStore('snapshots').get(snapshotKey(key,active.value))
      if (!staged) return abort(tx,new Error('Active capture snapshot is missing'))
      validateSnapshot(scope,staged.snapshot)
      const snapshot=staged.snapshot
      const captured=instant(input.capturedAt,'capturedAt')
      if (captured < instant(snapshot.preparedAt,'preparedAt') || captured > instant(snapshot.sessionEndsAt,'sessionEndsAt')) return abort(tx,new Error('Capture is outside the prepared session window'))
      const normalizedKey={...input.recordKey,activityId:input.recordKey.activityId ?? '0'}
      const scopeIds=snapshot.resources
      if (!scopeIds.participants.some(row=>row.id===normalizedKey.participantId) || !scopeIds.criteria.some(row=>row.id===normalizedKey.criterionId && (snapshot.scorePayloadVersion === 2 || row.scoringMode===input.payload.scoringMode)) ||
        (normalizedKey.activityId!=='0' && (!scopeIds.activities.some(row=>row.id===normalizedKey.activityId) || !scopeIds.activityCriteria.some(row=>row.activityId===normalizedKey.activityId && row.criterionId===normalizedKey.criterionId)))) {
        return abort(tx,new Error('Record is outside the prepared capture scope'))
      }
      const fullKey=recordKey(scope,normalizedKey)
      const cursor=await mutations.index('by-record-order').openCursor(IDBKeyRange.bound([key,fullKey,0],[key,fullKey,Number.MAX_SAFE_INTEGER]),'prev')
      const previous=cursor?.value
      if (previous && (previous.state.status==='attention' || previous.state.status==='resolved' && previous.state.resolution==='local')) return abort(tx,new Error('Review this record before another capture'))
      if (previous?.state.status==='pending' && previous.state.mutation.snapshotId!==snapshot.snapshotId) return abort(tx,new Error('Pending record belongs to another snapshot'))
      const baseRow=[...scopeIds.scores,...scopeIds.tombstones].find(row=>recordKey(scope,{participantId:row.recordKey.participantId,criterionId:row.recordKey.criterionId,sessionId:row.recordKey.sessionId,activityId:row.recordKey.activityId})===fullKey)
      const baseRevision=baseRow?.revision ?? null
      const predecessorMutationId=previous?.state.status==='resolved' || previous?.state.mutation.snapshotId!==snapshot.snapshotId ? null : previous?.id ?? null
      const metadata=tx.objectStore('metadata'), counter=await metadata.get('nextSequence')
      const sequence=Number(counter?.value ?? 0)+1
      if (!Number.isSafeInteger(sequence)) return abort(tx,new Error('Capture sequence exhausted'))
      const wireMutation: OfflineWireMutation={clientMutationId:id,sequence,recordKey:normalizedKey,baseRevision,predecessorMutationId,
        configurationRevision:snapshot.configurationRevision,capturedAt:input.capturedAt,payload:input.payload}
      const batch: OfflineWireBatch={schemaVersion:1,snapshotId:snapshot.snapshotId,deviceId:input.deviceId,dataEpoch:snapshot.dataEpoch,mutations:[wireMutation]}
      if (new TextEncoder().encode(JSON.stringify(batch)).length > 128 * 1024) return abort(tx,new Error('One capture exceeds the sync batch byte limit'))
      const payloadHash=sha256Hex(canonicalOfflineCommand(batch,wireMutation,scope.effectiveActorId,scope.eventId))
      const mutation: StoredMutationV2={scope,snapshotId:snapshot.snapshotId,dataEpoch:snapshot.dataEpoch,deviceId:input.deviceId,wireMutation,payloadHash}
      await metadata.put({key:'nextSequence',value:sequence})
      await mutations.add({id,scopeKey:key,recordKey:fullKey,sequence,state:{status:'pending',mutation}})
      await tx.done
      return clone(mutation)
    } catch (error) {
      try { tx.abort() } catch { /* Already complete or aborted. */ }
      try { await tx.done } catch { /* Observe rejection. */ }
      throw error
    }
  }
  async list(scope: CaptureScopeV2): Promise<MutationStateV2[]> {
    const key=captureScopeKeyV2(clone(scope)), db=await this.db()
    const rows=await db.getAllFromIndex('mutations','by-scope-order',IDBKeyRange.bound([key,0],[key,Number.MAX_SAFE_INTEGER]))
    for (const row of rows) if (row.scopeKey!==key || captureScopeKeyV2(row.state.mutation.scope)!==key || row.sequence!==row.state.mutation.wireMutation.sequence) throw new Error('Stored capture scope or sequence mismatch')
    return clone(rows.map(row=>row.state))
  }
  async markAttention(scope: CaptureScopeV2, clientMutationId: string, reason: string): Promise<void> {
    required(reason,'reason')
    const key=captureScopeKeyV2(clone(scope)), tx=(await this.db()).transaction('mutations','readwrite',{durability:'strict'})
    const row=await tx.store.get(clientMutationId)
    if (!row || row.scopeKey!==key || row.state.status!=='pending') return abort(tx,new Error('No matching pending mutation'))
    row.state={status:'attention',mutation:row.state.mutation,reason}
    await tx.store.put(row); await tx.done
  }
  async resolveAttention(scope: CaptureScopeV2, clientMutationId: string, choice: ResolveAttentionV2): Promise<StoredMutationV2 | undefined> {
    const stableScope=clone(scope), stable=clone(choice), key=captureScopeKeyV2(stableScope)
    uuid(clientMutationId,'clientMutationId'); uuid(stable.expectedSnapshotId,'expectedSnapshotId')
    const coveredMutationIds=stable.coveredMutationIds ?? [clientMutationId]
    if (!Array.isArray(coveredMutationIds) || !coveredMutationIds.length || coveredMutationIds.some(id=>!UUID.test(id))) throw new Error('Explicit covered mutation IDs are invalid')
    if (stable.choice==='local') {
      uuid(stable.clientMutationId,'replacement clientMutationId')
      required(stable.deviceId,'deviceId')
      if (stable.deviceId.length>128) throw new Error('deviceId exceeds 128 characters')
      instant(stable.capturedAt,'capturedAt')
    }
    const tx=(await this.db()).transaction(['metadata','snapshots','mutations'],'readwrite',{durability:'strict'})
    try {
      const mutations=tx.objectStore('mutations'), old=await mutations.get(clientMutationId)
      if (!old || old.scopeKey!==key || old.state.status!=='attention') return abort(tx,new Error('No matching attention item'))
      const original=old.state.mutation
      const active=await tx.objectStore('metadata').get(activeKey(key))
      if (active?.value!==stable.expectedSnapshotId || active.value===original.snapshotId) return abort(tx,new Error('A newly prepared active snapshot is required for review'))
      const staged=await tx.objectStore('snapshots').get(snapshotKey(key,stable.expectedSnapshotId))
      if (!staged) return abort(tx,new Error('Review snapshot is missing'))
      validateSnapshot(stableScope,staged.snapshot)
      const sameRecord=await mutations.index('by-record-order').getAll(IDBKeyRange.bound([key,old.recordKey,0],[key,old.recordKey,Number.MAX_SAFE_INTEGER]))
      if (sameRecord.some(row=>row.state.status==='pending')) return abort(tx,new Error('Resolve pending work on this record first'))
      const attention=sameRecord.filter(row=>row.state.status==='attention')
      if (attention[attention.length-1]?.id!==clientMutationId ||
        JSON.stringify(attention.map(row=>row.id))!==JSON.stringify(coveredMutationIds)) {
        return abort(tx,new Error('Review every attention item on this record in sequence'))
      }
      const resolvedAt=new Date().toISOString()
      if (stable.choice==='server') {
        for (const row of attention) {
          if (row.state.status!=='attention') continue
          row.state={status:'resolved',mutation:row.state.mutation,resolution:'server',resolvedAt,result:row.state.result}
          await mutations.put(row)
        }
        await tx.done
        return undefined
      }
      const newId=stable.clientMutationId
      if (newId===clientMutationId || await mutations.get(newId)) return abort(tx,new Error('Replacement mutation ID already exists'))
      const snapshot=staged.snapshot, wireKey=original.wireMutation.recordKey
      const captured=instant(stable.capturedAt,'capturedAt')
      if (captured<instant(snapshot.preparedAt,'preparedAt') || captured>instant(snapshot.sessionEndsAt,'sessionEndsAt')) return abort(tx,new Error('Review choice is outside the new snapshot capture window'))
      if (!snapshot.resources.participants.some(row=>row.id===wireKey.participantId) ||
        !snapshot.resources.criteria.some(row=>row.id===wireKey.criterionId && (snapshot.scorePayloadVersion === 2 || row.scoringMode===original.wireMutation.payload.scoringMode)) ||
        (wireKey.activityId!=='0' && (!snapshot.resources.activities.some(row=>row.id===wireKey.activityId) || !snapshot.resources.activityCriteria.some(row=>row.activityId===wireKey.activityId && row.criterionId===wireKey.criterionId)))) {
        return abort(tx,new Error('Record is no longer eligible for a local review choice'))
      }
      const base=[...snapshot.resources.scores,...snapshot.resources.tombstones].find(row=>recordKey(stableScope,{participantId:row.recordKey.participantId,criterionId:row.recordKey.criterionId,sessionId:row.recordKey.sessionId,activityId:row.recordKey.activityId})===old.recordKey)
      const metadata=tx.objectStore('metadata'), counter=await metadata.get('nextSequence'), sequence=Number(counter?.value ?? 0)+1
      if (!Number.isSafeInteger(sequence)) return abort(tx,new Error('Capture sequence exhausted'))
      const wireMutation: OfflineWireMutation={...original.wireMutation,clientMutationId:newId,sequence,baseRevision:base?.revision ?? null,
        predecessorMutationId:null,configurationRevision:snapshot.configurationRevision,capturedAt:stable.capturedAt}
      const batch: OfflineWireBatch={schemaVersion:1,snapshotId:snapshot.snapshotId,deviceId:stable.deviceId,dataEpoch:snapshot.dataEpoch,mutations:[wireMutation]}
      if (new TextEncoder().encode(JSON.stringify(batch)).length > 128 * 1024) return abort(tx,new Error('One capture exceeds the sync batch byte limit'))
      const replacement: StoredMutationV2={scope:stableScope,snapshotId:snapshot.snapshotId,dataEpoch:snapshot.dataEpoch,deviceId:stable.deviceId,wireMutation,
        payloadHash:sha256Hex(canonicalOfflineCommand(batch,wireMutation,stableScope.effectiveActorId,stableScope.eventId))}
      for (const row of attention) {
        if (row.state.status!=='attention') continue
        row.state={status:'resolved',mutation:row.state.mutation,resolution:'local',resolvedAt,replacementMutationId:newId,result:row.state.result}
      }
      await metadata.put({key:'nextSequence',value:sequence})
      for (const row of attention) await mutations.put(row)
      await mutations.add({id:newId,scopeKey:key,recordKey:old.recordKey,sequence,state:{status:'pending',mutation:replacement}})
      await tx.done
      return clone(replacement)
    } catch (error) {
      try { tx.abort() } catch { /* Already complete or aborted. */ }
      try { await tx.done } catch { /* Observe rejection. */ }
      throw error
    }
  }
  async applyResults(scope: CaptureScopeV2, results: CaptureSyncResultV2[]): Promise<void> {
    const key=captureScopeKeyV2(clone(scope)), stable=clone(results), ids=new Set<string>()
    for (const result of stable) {
      if (ids.has(result.clientMutationId) || !['applied','alreadyApplied','conflict','rejected','dependencyPending'].includes(result.outcome)) throw new Error('Invalid or duplicate sync result')
      uuid(result.clientMutationId,'clientMutationId')
      ids.add(result.clientMutationId)
      const unreceiptedReuse=result.outcome==='rejected' && result.code==='MUTATION_ID_REUSE' && !result.serverReceiptId && !result.payloadHash
      if (result.outcome!=='dependencyPending' && !unreceiptedReuse) {
        uuid(result.serverReceiptId ?? '', 'serverReceiptId')
        if (!/^[0-9a-f]{64}$/.test(result.payloadHash ?? '')) throw new Error('Result hash is invalid')
        instant(result.serverReceivedAt ?? '', 'serverReceivedAt')
        if (result.recordRevision !== null && result.recordRevision !== undefined && !REVISION.test(result.recordRevision)) throw new Error('Result revision is invalid')
        if (result.outcome==='applied' || result.outcome==='alreadyApplied') {
          if (!result.recordRevision || result.recordRevision==='0' || !result.record) throw new Error('Applied result lacks record and positive revision')
          uuid(result.record.id ?? '', 'record.id')
        }
      }
      canonical(result)
    }
    const tx=(await this.db()).transaction('mutations','readwrite',{durability:'strict'})
    const updates: StoredRow[]=[]
    for (const result of stable) {
      const row=await tx.store.get(result.clientMutationId)
      if (!row || row.scopeKey!==key) return abort(tx,new Error('Sync result does not match a durable mutation'))
      if (result.outcome==='dependencyPending') continue
      if (result.outcome==='rejected' && result.code==='MUTATION_ID_REUSE' && !result.serverReceiptId && !result.payloadHash) {
        if (row.state.status==='confirmed' || row.state.status==='resolved' || row.state.status==='attention' && row.state.result?.serverReceiptId) return abort(tx,new Error('Refusal conflicts with settled mutation'))
        row.state={status:'attention',mutation:row.state.mutation,reason:'MUTATION_ID_REUSE',result}
        updates.push(row)
        continue
      }
      if (row.state.mutation.payloadHash!==result.payloadHash) return abort(tx,new Error('Sync result hash does not match mutation'))
      if (result.outcome==='applied' || result.outcome==='alreadyApplied') {
        const wire=row.state.mutation.wireMutation, record=result.record!
        if (record.participantId!==wire.recordKey.participantId || record.criterionId!==wire.recordKey.criterionId ||
          record.sessionId!==wire.recordKey.sessionId || (record.activityId ?? '0')!==(wire.recordKey.activityId ?? '0') ||
          record.staffId!==row.state.mutation.scope.effectiveActorId) {
          return abort(tx,new Error('Applied result record does not match durable command'))
        }
      }
      if (row.state.status==='resolved') return abort(tx,new Error('Resolved mutation cannot accept a new result'))
      if (row.state.status==='confirmed' || row.state.status==='attention' && row.state.result) {
        const old=row.state.status==='confirmed'?row.state.receipt:row.state.result!
        const normalize=(value: CaptureSyncResultV2): string=>canonical({...value,outcome:value.outcome==='alreadyApplied'?'applied':value.outcome})
        if (normalize(old)!==normalize(result)) return abort(tx,new Error('Conflicting result for settled mutation'))
        continue
      }
      if (row.state.status==='attention') return abort(tx,new Error('Manual attention requires explicit resolution'))
      row.state=result.outcome==='applied' || result.outcome==='alreadyApplied'
        ? {status:'confirmed',mutation:row.state.mutation,receipt:result}
        : {status:'attention',mutation:row.state.mutation,reason:result.code ?? result.outcome,result}
      updates.push(row)
    }
    for (const row of updates) await tx.store.put(row)
    await tx.done
  }
  async getLegacyRecoveryCount(scope: CaptureScopeV2): Promise<number | null> {
    const stable=clone(scope); captureScopeKeyV2(stable)
    if (typeof indexedDB.databases!=='function') return null
    const databases=await indexedDB.databases()
    if (!databases.some(row=>row.name==='coachsight-capture-v1')) return 0
    const db=await openDB('coachsight-capture-v1')
    try {
      if (!db.objectStoreNames.contains('mutations')) return null
      const oldScope=JSON.stringify([stable.apiOrigin,stable.realActorId,stable.effectiveActorId,stable.clubId,stable.seasonId,stable.eventId])
      const rows=await db.getAll('mutations') as Array<{scopeKey?: string; state?: {status?: string}}>
      return rows.filter(row=>row.scopeKey===oldScope && row.state?.status!=='confirmed').length
    } finally { db.close() }
  }
  close(): void { void this.dbPromise?.then(db=>db.close()); this.dbPromise=undefined }
}
